import {
    merge,
    filter,
    flow,
    identity,
    includes,
    join,
    partialRight,
    toLower,
    values,
    isObject,
    round,
} from "lodash";
import { normalize } from 'normalizr';
import { camelizeKeys } from 'humps';
import moment from "moment";

export const contextSearch = (array, substr) => {
    const flatObjects = (seq) => {
        return seq.map((item) => {
            if ( isObject(item) ) {
                return join(values(item));
            }

            return item
        });
    };

    return filter(array, flow(
        identity,
        values,
        flatObjects,
        join,
        toLower,
        partialRight(includes, substr)
    ));
};

export const createMarkup = html => {
    return { __html: html }
};

const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const highlightText = (text, context) => {
    return(text
        ? context
            ? text.replace(new RegExp(escapeRegExp(context), "gi"), `<span class="sp-table-highlighted-text">$&</span>`)
            : text.replace(/(<([^>]+)>)/gi, "")
        : text
    )
};

export const parseJSON = (response) => {
    const apiServerVersion = response.headers.get("x-thermos-version");
    const contentType = response.headers.get("content-type");
    const isFile = contentType.indexOf("text/html") > -1 || contentType.indexOf("application/pdf") > -1 || contentType.indexOf("text/csv") > -1;

    if (apiServerVersion && apiServerVersion !== process.env.REACT_APP_API_VERSION) {
        return new Promise(resolve => resolve({
                ok: false,
                json: {
                    code: 409,
                    message: `Version ${apiServerVersion} of the server is incompatible with the client version ${process.env.REACT_APP_API_VERSION}`},
            }));
    };
    if (response.status === 200 || response.status === 201) {
        return isFile
            ? new Promise(resolve => response.blob().then(blob =>
                resolve({
                    status: response.status,
                    ok: response.ok,
                    json: {
                        url: URL.createObjectURL(blob)
                    }
                })))
            : new Promise(resolve => response.json().then(json =>
                resolve({
                    status: response.status,
                    ok: response.ok,
                    json,
                })));
    }
    else if (response.status === 204) {
        return new Promise(resolve => response.text().then(text =>
            resolve({
                status: response.status,
                ok: response.ok,
                json: {},
            })));
    }
    else if (response.status === 401 && response.statusText === 'UNAUTHORIZED') {
        const href = window.location.href;
        if (href.indexOf("/login") < 0 && href.indexOf("/logout") < 0) {
            window.location = `/#/logout?from=${encodeURIComponent(href.substring(href.indexOf("#") + 1, href.length))}`;
        }
        return new Promise(resolve => resolve({
            status: response.status,
            ok: false,
            json: {
                code: response.status,
                message: "Session timeout",
                success: false
            },
        }));
    };

    return new Promise(resolve => response.text().then(text => {
        let reason = '';

        try {
            var json = JSON.parse(text);
            if (json && typeof json === 'object' && json !== null) {
                reason = 'reason' in json ? json.reason : ('message' in json ? json.message : 'Unknown error');
            }
        } catch (err) {
            reason = "Unknown error";
         };

        return resolve({
            status: response.status,
            ok: false,
            json: {
                code: response.status,
                message: reason,
                success: false
            },
        });
    }));
};

export const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
}

export const fetchData = (endpoint, newoptions, schema={}) => {
        let options = {
            credentials: 'include',
            headers: {
                'X-CSRF-TOKEN': getCookie('csrf_access_token'),
            }
        };
        options = merge(options, newoptions);

        return new Promise((resolve, reject) => {
            fetch(endpoint, options)
                .then(parseJSON)
                .then(response => {
                    if (response.ok) {
                        const camelizedJson = camelizeKeys(response.json);
                        return resolve(Object.assign({}, normalize(camelizedJson, schema)));
                    };
                    return reject(response.json);
                })
                .catch(error => {
                    return reject({
                        code: error.code,
                        message: error.message,
                        success: false
                    })
                });
        });
};

export const isNone = (value) => {
    return toLower(value) === 'none';
};

export const noneToNull = (value) => {
    return toLower(value) === 'none' ? null : value;
};

export const formatDate = (date, format) => moment.utc(date).format(format);

export const fixPackageSize = (bytes) => {
    if (bytes == 0) return '0 byte';
    let sizes = ['bytes', 'KB', 'MB', 'GB', 'TB'];
    let i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    if ((Math.ceil(bytes / Math.pow(1024, i))).toString().length > 3) i += 1;
    return round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
};

export const numberWithSpaces = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

export const normilizeFilename = (filename) => {
    // remove non-ascii characters
    let str = filename.replace(/[^\x00-\x7F]/g, "");
    // replace white spaces with underscores and remove non-secure characters
    str = str.split(" ").join("_").replace(/[^A-Za-z0-9_.-]/g, "");
    // remove leading and trailing dots and underscores
    str = str.replace(/^[._]+|[._]+$/g, "");

    return str;
};

export const replaceBrackets = (text) => {
    text = text.replace("<", "&lt;");
    text = text.replace(">", "&gt;");
    return text;
};